/*
 * Wishlist (Swym Wishlist Plus) — shared client wiring for every wishlist
 * button in the theme. We render our own buttons (over gallery images, on
 * product cards, on featured-product, and inside Algolia result cards) and
 * drive Swym programmatically via its JS API, rather than using Swym's
 * auto-injected markup.
 *
 * A button opts in by carrying one of the hook attributes below plus:
 *   data-product-id   → Swym empi (numeric product id)   [preferred]
 *   data-variant-id   → Swym epi  (numeric variant id)   [optional]
 *   data-product-url  → Swym du   (product URL)           [fallback key]
 *
 * State is keyed by product (empi, or the URL path when no id is available),
 * so the mobile + desktop hearts for the same product stay in sync, and a
 * product saved from a card shows as saved on its PDP.
 *
 * Active state is expressed as the `data-wishlist-active` attribute +
 * aria-pressed; the fill styling lives in each button's Tailwind classes
 * (data-wishlist-active:[&_path]:fill-current).
 */
(function () {
  'use strict';

  var SELECTOR = '[data-pdp-wishlist],[data-fp-wishlist],[data-mega-nav-wishlist],[data-wishlist-btn]';

  var swat = null;              // Swym API instance once ready
  var ready = false;
  var saved = new Set();        // keys: 'p:<empi>' and 'u:<path>'
  var pending = new Set();      // product keys with an in-flight add/remove

  function urlPath(u) {
    if (!u) return '';
    try {
      return new URL(u, window.location.origin).pathname.replace(/\/+$/, '');
    } catch (e) {
      return String(u).split('?')[0].replace(/\/+$/, '');
    }
  }

  function absoluteUrl(u) {
    if (!u) return '';
    try { return new URL(u, window.location.origin).href; } catch (e) { return u; }
  }

  function btnData(btn) {
    var pid = btn.getAttribute('data-product-id') || '';
    var vid = btn.getAttribute('data-variant-id') || '';
    var url = btn.getAttribute('data-product-url') || btn.getAttribute('href') || '';
    return {
      empi: pid ? Number(pid) : null,
      epi: vid ? Number(vid) : null,
      du: absoluteUrl(url),
      // Display fields Swym needs to render the item in the wishlist panel.
      // Without these the item is still counted (badge) but the panel can't
      // build its product card, so it appears empty.
      iu: btn.getAttribute('data-product-image') || '',
      dt: btn.getAttribute('data-product-title') || '',
      pr: btn.getAttribute('data-product-price') || '',
      pKey: pid ? 'p:' + pid : 'u:' + urlPath(url),
    };
  }

  function isSaved(d) {
    if (d.empi != null && saved.has('p:' + d.empi)) return true;
    if (d.du && saved.has('u:' + urlPath(d.du))) return true;
    return false;
  }

  function setBtnState(btn, on) {
    if (on) btn.setAttribute('data-wishlist-active', '');
    else btn.removeAttribute('data-wishlist-active');
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  function paintAll() {
    document.querySelectorAll(SELECTOR).forEach(function (btn) {
      setBtnState(btn, isSaved(btnData(btn)));
    });
  }

  // Swym's fetch payload shape can vary slightly across versions; be liberal
  // about where the item array lives and which id keys are present.
  function rebuildSet(lists) {
    saved = new Set();
    var arr = Array.isArray(lists) ? lists : (lists ? [lists] : []);
    arr.forEach(function (list) {
      var items = (list && (list.listcontents || list.items || list.products)) || [];
      items.forEach(function (it) {
        if (it == null) return;
        if (it.empi != null) saved.add('p:' + it.empi);
        if (it.du) saved.add('u:' + urlPath(it.du));
      });
    });
  }

  function refresh() {
    if (!swat || !swat.fetch) return;
    swat.fetch(function (lists) { rebuildSet(lists); paintAll(); }, function () {});
  }

  function toggle(btn) {
    if (!ready || !swat) return;
    var d = btnData(btn);
    if (!d.du && d.empi == null) return;
    if (pending.has(d.pKey)) return;

    var product = { du: d.du };
    if (d.empi != null) product.empi = d.empi;
    if (d.epi != null) product.epi = d.epi;
    if (d.iu) product.iu = absoluteUrl(d.iu);
    if (d.dt) product.dt = d.dt;
    var prNum = parseFloat(d.pr);
    if (!isNaN(prNum)) product.pr = prNum;

    var wasSaved = isSaved(d);
    pending.add(d.pKey);

    // Optimistic: flip local state + repaint immediately, reconcile on callback.
    function flip(add) {
      if (d.empi != null) { add ? saved.add('p:' + d.empi) : saved.delete('p:' + d.empi); }
      if (d.du) { add ? saved.add('u:' + urlPath(d.du)) : saved.delete('u:' + urlPath(d.du)); }
    }
    flip(!wasSaved);
    paintAll();

    var settle = function () { pending.delete(d.pKey); refresh(); };
    if (wasSaved) swat.removeFromWishList(product, settle, settle);
    else swat.addToWishList(product, settle, settle);
  }

  // Open/toggle Swym's built-in wishlist panel from the header launcher.
  // The guaranteed path is Swym's "Custom Wishlist Modal" feature bound to
  // [data-header-wishlist] via the Swym dashboard selector; the calls below
  // are best-effort programmatic opens (no-ops when the method is absent),
  // with an optional data-wishlist-url page fallback.
  function openWishlistPanel(trigger) {
    try {
      var ui = swat && (swat.ui || swat.wishlistUI);
      if (ui) {
        if (typeof ui.toggleWishlist === 'function') { ui.toggleWishlist(); return true; }
        if (typeof ui.openWishlist === 'function') { ui.openWishlist(); return true; }
        if (typeof ui.renderWishlist === 'function') { ui.renderWishlist(); return true; }
      }
      if (swat && typeof swat.showWishListPopup === 'function') { swat.showWishListPopup(); return true; }
    } catch (e) {}
    var href = trigger.getAttribute('data-wishlist-url');
    if (href) { window.location.href = href; return true; }
    return false;
  }

  // Swipe/drag guard. The PDP gallery hearts sit on top of a native touch-scroll
  // carousel (one heart per slide, over the image), so a swipe can end as a
  // synthetic `click` on a button and silently add the product. Track how far
  // the pointer travelled between down and up; a click that is really the tail
  // of a swipe/drag is suppressed. Capture phase so we record before the click.
  var DRAG_THRESHOLD = 10; // px
  var downX = 0, downY = 0, wasDrag = false;
  document.addEventListener('pointerdown', function (e) {
    downX = e.clientX; downY = e.clientY; wasDrag = false;
  }, true);
  document.addEventListener('pointerup', function (e) {
    wasDrag = Math.abs(e.clientX - downX) > DRAG_THRESHOLD ||
              Math.abs(e.clientY - downY) > DRAG_THRESHOLD;
  }, true);

  // Click via delegation so dynamically-injected cards (Algolia) work with no
  // rebinding. preventDefault stops any wrapping card link from navigating.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var launcher = e.target.closest('[data-header-wishlist]');
    if (launcher) {
      e.preventDefault();
      openWishlistPanel(launcher);
      return;
    }

    var btn = e.target.closest(SELECTOR);
    if (!btn) return;
    e.preventDefault();
    if (wasDrag) return; // swipe, not a tap — don't toggle
    toggle(btn);
  });

  // Repaint when new buttons appear (Algolia results, deferred sections).
  var scheduled = false;
  if (window.MutationObserver && document.body) {
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () { scheduled = false; paintAll(); });
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (api) {
    swat = api;
    ready = true;
    refresh();
    try {
      var evt = api.evtLayer;
      var E = api.JSEvents || {};
      if (evt && evt.addEventListener) {
        if (E.addedToWishlist) evt.addEventListener(E.addedToWishlist, refresh);
        if (E.removedFromWishlist) evt.addEventListener(E.removedFromWishlist, refresh);
      }
    } catch (e) {}
  });
})();
